// Collars acquisition (CR-20260930-001 M3, TMHQ, frontend-only, pure): the pending ORDER BLOCKS of a
// Portfolio Hedging Analysis (exported by a live session, or orphaned by a dead one) as the Export
// window shows them in its ACQUIRE mode — the owner's ruling 2026-09-30: the acquisition window IS the
// Export window, plus a fill box per leg and an Acquire button.
//
// A block is ONE ToS order: priced and acquired AS A WHOLE (§6.5). Before = the group's live positions
// on the block's instruments (signed, face value). Transaction = the block's changes, frozen at export.
// After = Before + Transaction. Nothing is diffed against a saved end-state.

import { parseOptionTicker } from '../../options-common/options.model';
import { CollarsBucketDto, CollarsPositionDto } from '../../shell-communication/collars-operations';
import { AnalysisChangeDto, AnalysisPendingBlockDto } from '../../shell-communication/analysis-session-operations';
import { PhaExportBlock, PhaExportGroup, PhaExportLeg } from '../../portfolio-hedging-analysis/pha-export-dialog.component';

const KIND_ASSET = 0;
const LEVEL_GROUP = 2;
const SECTION_ORDER: { [s: string]: number } = { Underlyings: 0, Premium: 1, Protection: 2 };

export interface AcquireGroupsContext {
   /** "Apple Inc" for AAPL, or the ticker itself when unknown. */
   displayNameOf: (ticker: string) => string;
   /** "Exported 30-Sep-26 05:17:14 PM" in the user's zone, from the ISO instant. */
   exportedTextOf: (isoUtc: string) => string;
   /** Narrow to these blocks (a single element's scope); empty = every pending block. */
   blockIds?: string[];
   /**
    * The Collars GROUPS under the clicked scope (a group: itself; a combo: its groups; a portfolio: every
    * group of every combo) — a block shows only when it books into one of them. Empty = no narrowing.
    * Owner 2026-09-30: the window respects the scope it was opened on.
    */
   groupIds?: string[];
}

/**
 * The pending blocks as Export-window groups: section → underlying (a stock and its options are two
 * groups) → blocks by sequence ("Main Order", "Linked #1"… for options; a stock's block is unnamed).
 * A block whose changes carry no Collars group is skipped (it was never exported).
 */
export function buildAcquireGroups(
   pending: AnalysisPendingBlockDto[],
   positions: CollarsPositionDto[],
   bucketById: { [id: string]: CollarsBucketDto },
   ctx: AcquireGroupsContext
): PhaExportGroup[] {
   const wanted = new Set<string>((ctx.blockIds || []).filter(id => !!id));
   const wantedGroups = new Set<string>((ctx.groupIds || []).filter(id => !!id));
   const groups: PhaExportGroup[] = [];
   const seqByBlock: { [blockId: string]: number } = {};

   (pending || []).forEach(pb => {
      const block = pb && pb.block;
      if (!block || !block.changes || block.changes.length === 0) { return; }
      if (wanted.size > 0 && !wanted.has(block.blockId)) { return; }
      const groupId = block.changes[0].groupBucketId;
      if (!groupId) { return; }
      if (wantedGroups.size > 0 && !wantedGroups.has(groupId)) { return; }
      const group = bucketById[groupId];
      if (group && group.level !== LEVEL_GROUP) { return; }

      const isAsset = block.changes.every(c => c.kind === KIND_ASSET);
      const underlying = underlyingOf(block.changes[0]);
      const key = block.section + '|' + underlying + '|' + (isAsset ? 'S' : 'O');
      let g = groups.find(x => x.key === key);
      if (!g) {
         const name = ctx.displayNameOf(underlying);
         g = {
            key,
            section: block.section,
            title: name || underlying,
            isAsset,
            blocks: []
         };
         groups.push(g);
      }

      // Before: the group's live positions on the block's instruments (net != 0; not interest, not archived).
      const codes: { [code: string]: true } = {};
      block.changes.forEach(c => { codes[c.tradingInstrumentCode] = true; });
      const netByCode: { [code: string]: number } = {};
      (positions || []).forEach(p => {
         if (p.groupId === groupId && codes[p.tradingInstrumentCode] && !p.isInterest && !p.isArchived && p.netPosition !== 0) {
            netByCode[p.tradingInstrumentCode] = (netByCode[p.tradingInstrumentCode] || 0) + p.netPosition;
         }
      });

      const runningAfter: { [code: string]: number } = { ...netByCode };
      const legs: PhaExportLeg[] = block.changes.map(c => {
         const before = netByCode[c.tradingInstrumentCode] || 0;
         const after = (runningAfter[c.tradingInstrumentCode] || 0) + c.side * c.qty;
         runningAfter[c.tradingInstrumentCode] = after;
         const attrs = optionAttrsOf(c);
         return {
            kind: c.kind,
            ticker: c.kind === KIND_ASSET ? c.tradingInstrumentCode : underlying,
            type: attrs.type || '',
            expiration: attrs.expiration || '',
            strike: attrs.strike != null ? attrs.strike : null,
            beforeQty: before,
            afterQty: after,
            tradeSide: c.side > 0 ? 1 : -1,
            tradeQty: c.qty,
            code: c.tradingInstrumentCode,
            changeId: c.changeId,
            groupBucketId: groupId,
            fillPx: null,
            marketPx: null
         } as PhaExportLeg;
      });

      seqByBlock[block.blockId] = block.sequence;
      g.blocks.push({
         blockId: block.blockId,
         name: '',
         status: block.status,
         legs,
         blockedReason: '',
         exportedText: block.exportedAtUtc ? ctx.exportedTextOf(block.exportedAtUtc) : '',
         sessionId: pb.sessionId,
         isOrphaned: !!pb.isOrphaned,
         orderText: block.orderText || ''
      } as PhaExportBlock);
   });

   groups.forEach(g => {
      g.blocks.sort((a, b) => (seqByBlock[a.blockId] || 0) - (seqByBlock[b.blockId] || 0));
      if (!g.isAsset) {
         g.blocks.forEach((b, i) => { b.name = (b.orderText || '').indexOf(' TRG BY') >= 0 ? 'Linked #' + i : 'Order ' + (i + 1); });
      }
   });
   groups.sort((a, b) => {
      const s = (SECTION_ORDER[a.section] || 0) - (SECTION_ORDER[b.section] || 0);
      if (s !== 0) { return s; }
      return a.title.localeCompare(b.title);
   });
   return groups;
}

function underlyingOf(c: AnalysisChangeDto): string {
   if (c.kind === KIND_ASSET) { return c.tradingInstrumentCode; }
   const t = parseOptionTicker(c.tradingInstrumentCode);
   return t ? t.underlying : c.tradingInstrumentCode;
}

function optionAttrsOf(c: AnalysisChangeDto): { type?: string; strike?: number; expiration?: string } {
   if (c.kind === KIND_ASSET) { return {}; }
   const t = parseOptionTicker(c.tradingInstrumentCode);
   return {
      type: c.optionType || (t ? t.type : undefined),
      strike: c.strike != null ? c.strike : (t ? t.strike : undefined),
      expiration: c.expiration || (t ? t.expiration : undefined)
   };
}
